import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { haptic } from '../../../lib/haptic';
import { env } from '../../../lib/env';
import { SignupProvider, useSignup } from './SignupContext';
import { SIGNUP_STEPS, type SignupStep } from './types';
import { ProfileStep } from './steps/ProfileStep';
import { DlStep } from './steps/DlStep';
import { AadhaarStep } from './steps/AadhaarStep';
import { PanStep } from './steps/PanStep';

function stepTitleKey(step: SignupStep): string {
  switch (step) {
    case 'profile': return 'signupHeader.profileTitle';
    case 'dl': return 'signupHeader.dlTitle';
    case 'aadhaar': return 'signupHeader.aadhaarTitle';
    case 'pan': return 'signupHeader.panTitle';
  }
}

function StepBody({ step }: { step: SignupStep }) {
  switch (step) {
    case 'profile': return <ProfileStep />;
    case 'dl': return <DlStep />;
    case 'aadhaar': return <AadhaarStep />;
    case 'pan': return <PanStep />;
  }
}

function ProgressDots({ activeIndex }: { activeIndex: number }) {
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={SIGNUP_STEPS.length}
      aria-valuenow={activeIndex + 1}
      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
    >
      {SIGNUP_STEPS.map((_, idx) => {
        const completed = idx < activeIndex;
        const current = idx === activeIndex;
        const size = current ? 12 : 10;
        const bg = completed
          ? 'var(--green-700)'
          : current
            ? 'var(--green-700)'
            : 'var(--color-border)';
        return (
          <span
            key={idx}
            aria-hidden
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: `${size}px`,
              height: `${size}px`,
              borderRadius: '9999px',
              background: bg,
              transition: 'background-color 150ms ease, width 150ms ease, height 150ms ease',
            }}
          >
            {completed ? (
              <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden>
                <path d="M1.5 4.2 L3.3 6 L6.5 2.5" stroke="white" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

function ShellInner() {
  const { t } = useTranslation('auth');
  const nav = useNavigate();
  const location = useLocation();
  const { step, setStep, setField } = useSignup();

  // Seed wizard fields from the invite-redeem prefill so the driver only has
  // to confirm what ops already entered in connect.fleetup.in:
  // - name, secondaryPhone, DL number/expiry, Aadhaar number, PAN number
  // - photo (fetched from the public /v1/drivers/:id/photo endpoint and
  //   converted to a data URL so it lives alongside any photo the driver
  //   takes from gallery — both end up as photoDataUrl)
  useEffect(() => {
    const prefill = (location.state as any)?.prefill;
    if (!prefill) return;

    if (prefill.name?.trim()) {
      setField('name', prefill.name.trim());
    }
    if (prefill.secondaryPhone) {
      // Stored as +91XXXXXXXXXX; the input expects the 10-digit body without prefix.
      const local = String(prefill.secondaryPhone).replace(/^\+91/, '').replace(/\D/g, '');
      if (local) setField('phone', local);
    }
    if (prefill.drivingLicenseNumber) setField('dlNumber', prefill.drivingLicenseNumber);
    if (prefill.drivingLicenseExpiry) setField('dlExpiry', prefill.drivingLicenseExpiry);
    if (prefill.aadhaarNumber) setField('aadhaarNumber', prefill.aadhaarNumber);
    if (prefill.panNumber) setField('panNumber', prefill.panNumber);

    if (prefill.hasPhoto && prefill.id) {
      // Fetch as blob → data URL so the existing PhotoCapture preview renders
      // the ops-uploaded photo. Quiet failure (network, CORS, deleted photo)
      // just leaves the wizard's photo step empty — driver can re-capture.
      const apiBase = env().VITE_API_BASE.replace(/\/+$/, '');
      const url = `${apiBase}/v1/drivers/${prefill.id}/photo`;
      void (async () => {
        try {
          const r = await fetch(url);
          if (!r.ok) return;
          const blob = await r.blob();
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === 'string') setField('photoDataUrl', reader.result);
          };
          reader.readAsDataURL(blob);
        } catch {
          // ignore — wizard's photo step stays empty, driver can take a new one
        }
      })();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeIndex = SIGNUP_STEPS.indexOf(step);

  const onBack = () => {
    haptic.tap();
    if (activeIndex <= 0) {
      nav('/auth/invite');
      return;
    }
    const prev = SIGNUP_STEPS[activeIndex - 1];
    if (prev) setStep(prev);
  };

  return (
    <div
      data-theme="light"
      style={{
        // Match the 440px mobile-frame width used by AppShell (line 94) and
        // AuthScreenShell. Without these, #root's `align-items: center` centers
        // the column but the column shrink-wraps to its content — so as soon
        // as the photo step renders an <img> with a large intrinsic width,
        // the entire signup wizard bursts out of the mobile frame on desktop.
        width: '100%',
        maxWidth: 440,
        minHeight: '100dvh',
        background: 'var(--color-bg-page)',
        color: 'var(--color-text)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '16px 20px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-surface)',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label={t('signupHeader.back')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            padding: '6px 8px',
            margin: '-6px -8px',
            cursor: 'pointer',
            color: 'var(--color-text)',
            fontFamily: 'inherit',
            fontSize: '15px',
            fontWeight: 500,
            borderRadius: 'var(--radius-md)',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
            <path d="M12.5 4 L6.5 10 L12.5 16" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('signupHeader.back')}</span>
        </button>

        <ProgressDots activeIndex={activeIndex} />
      </header>

      <div
        style={{
          padding: '20px 20px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('signupHeader.stepLabel', { current: activeIndex + 1, total: SIGNUP_STEPS.length })}
        </p>
        <h1
          style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: 'var(--color-text)',
          }}
        >
          {t(stepTitleKey(step))}
        </h1>
      </div>

      <main
        style={{
          flex: 1,
          padding: '8px 20px 32px',
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <StepBody step={step} />
      </main>
    </div>
  );
}

export function SignupShell() {
  return (
    <SignupProvider>
      <ShellInner />
    </SignupProvider>
  );
}
